import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Form, Field } from 'react-final-form'
import { Button, DatePicker, Upload } from 'element-react'
import queryString from 'query-string'

import FormHeader from '../../components/user/includes/FormHeader'
import FormMessageTwoButtons from '../../components/user/includes/FormMessageTwoButtons'
import RequestDialog, {
    RequestType,
} from '../../components/user/includes/RequestDialog'
import { LabeledInputField } from '../../helpers/fieldHelper'
import CustomLoader from '../../components/user/includes/CustomLoader'
import {
    tagHotjarRecording,
    createValidator,
    sendEventToIntercom,
} from '../../helpers/util'
import {
    AppContainer,
    MainSectionDefault,
} from '../../components/views/includes/Wrappers'
import Spacer from '../../components/views/includes/Spacer'
import ErrorAlert from '../../components/views/includes/ErrorAlert'
import ShiptoDropdown from '../../components/views/includes/ShiptoDropdown'
import { logErrorOnServer } from '../../helpers/serverLogging'
import useRequest from '../../hooks/useRequest'
import useAuth from '../../hooks/useAuth'
import AppRoutes from '../../routes/appRoutes'
import { trackPartsRequestCreatedOnline } from '../../utils/sendGAAnalyticsEvent'

const validate = createValidator([
    'description',
    'make',
    'model',
    'quantity',
    'shipToAccountId',
])

const logError = logErrorOnServer('New Parts Request')

const PartRequestPage = props => {
    const { location } = props

    const {
        handleCreatePartsRequest,
        loadEquipment,
        clearAssetStore,
        make,
        model,
    } = useRequest()

    const initialValues = { make, model }

    const [equipmentSFId, setEquipmentSFId] = useState('')
    const [photoId, setPhotoId] = useState('')
    const [neededByDate, setNeededByDate] = useState(new Date())
    const [error, setError] = useState('')
    const [isLoading, setIsLoading] = useState(true)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [showNewRequestDialog, setShowNewRequestDialog] = useState(false)
    const [formSuccess, setFormSuccess] = useState(false)
    const { user, impersonatedBy } = useAuth()

    const selectOptions = user?.locationPreferences || []

    useEffect(() => {
        // Get the passed in equipment if we have one
        const values = queryString.parse(location.search)
        const equipmentId = values.equipmentId ? values.equipmentId : ''
        setEquipmentSFId(equipmentId)

        if (equipmentId) {
            loadEquipment({
                equipmentId: equipmentId,
                companyId: user?.company?.id,
            })
        }

        //Load locations
        tagHotjarRecording('Part Request')

        return () => {
            clearAssetStore()
        }
    }, [])

    const onCreatedPartRequestSuccess = response => {
        sendEventToIntercom('successful-parts-quote-request')
        setError('')
        trackPartsRequestCreatedOnline(response, user?.external_contact_id)
        setFormSuccess(true)
        setIsSubmitting(false)
    }

    const onCreatedPartRequestError = error => {
        const {
            errors,
            message = 'Please check your internet connection and try again.',
        } = error?.response?.data ?? {}
        const errorMessage =
            errors &&
            Array.isArray(errors.description) &&
            errors.description[0].length > 0
                ? errors.description[0]
                : errors &&
                  Array.isArray(errors.quantity) &&
                  errors.quantity[0].length > 0
                ? errors.quantity[0]
                : message
        setError(errorMessage)
        setIsSubmitting(false)
        logError(errorMessage)
    }

    const onSubmit = values => {
        setIsSubmitting(true)
        const params = {
            equipmentSFId,
            neededByDate,
            photoId,
            userId: user?.id,
            ...values,
        }
        handleCreatePartsRequest(
            params,
            onCreatedPartRequestSuccess,
            onCreatedPartRequestError,
        )
    }

    const handleImageSuccess = res => {
        document.getElementById('upload').innerHTML =
            '<img src="' + res.filepath + '"/>'
        document.getElementById('upload-btn-text').innerHTML = 'Replace Image'
        setError('')
        setIsLoading(false)
        setPhotoId(res?.filename)
    }

    const handleImageError = result => {
        const errorMessage =
            result.toString().indexOf('File is too large') > -1
                ? 'File is too large. 8MB Max'
                : result.toString().indexOf('422') > -1
                ? result.toString().substring(10)
                : 'Unable to upload file.'
        setError(errorMessage)
        setIsLoading(false)
        logError(errorMessage)
    }

    const showLoader = () => {
        setIsLoading(true)
    }

    let backLinkUrl =
        location?.state && location.state?.source
            ? location.state.source
            : '/requests'

    if (formSuccess) {
        return (
            <AppContainer className="form" whiteBodyBackground={true}>
                <FormHeader
                    headerTitle="Parts Quote Request"
                    backLink={backLinkUrl}
                />

                <Spacer size="small" />

                <FormMessageTwoButtons
                    firstButtonLabel="Submit Another Request"
                    firstButtonOnClick={() => setShowNewRequestDialog(true)}
                    messageContent="We will contact you shortly about your request."
                    messageTitle="Your Parts Request has been sent"
                    sencondButtonLabel="Return to Requests"
                    secondButtonLink={AppRoutes.requests()}
                />
                {/* Performance optimization - don't render until we need to display.
                This also fixes an issue with the dialog on IE11. */
                showNewRequestDialog && (
                    <RequestDialog
                        show={true}
                        cancel={() => setShowNewRequestDialog(false)}
                        equipmentId=""
                        linkToSelf={RequestType.PARTS}
                        onLinkToSelf={() => {
                            setShowNewRequestDialog(false)
                            setFormSuccess(false)
                        }}
                    />
                )}
            </AppContainer>
        )
    }

    return (
        <AppContainer className="form" whiteBodyBackground={true}>
            <FormHeader
                headerTitle="Parts Quote Request"
                backLink={backLinkUrl}
            />

            <Spacer size="small" />

            {(isLoading || isSubmitting) && (
                <MainSectionDefault>
                    <Spacer size="large" />
                    <CustomLoader></CustomLoader>
                </MainSectionDefault>
            )}

            <MainSectionDefault>
                <Form
                    onSubmit={onSubmit}
                    validate={validate}
                    initialValues={initialValues}
                >
                    {({
                        handleSubmit,
                        error: formError,
                        pristine,
                        submitting,
                    }) => (
                        <>
                            <div>
                                {formError ||
                                    (error && (
                                        <ErrorAlert
                                            error={formError || error}
                                        />
                                    ))}
                                {formError ||
                                    (error && (
                                        <strong>{formError || error}</strong>
                                    ))}
                            </div>
                            <form id="partsRequestForm" onSubmit={handleSubmit}>
                                <Field
                                    label="What do you need?"
                                    name="description"
                                    component={LabeledInputField}
                                    type="text"
                                    placeholder=""
                                    maxLength={100}
                                />

                                <Field
                                    label="Make"
                                    name="make"
                                    component={LabeledInputField}
                                    type="text"
                                    placeholder=""
                                    maxLength={100}
                                />

                                <Field
                                    label="Model"
                                    name="model"
                                    component={LabeledInputField}
                                    type="text"
                                    placeholder=""
                                    maxLength={100}
                                />

                                <label>When do you need it?</label>
                                <div className="input-container">
                                    <DatePicker
                                        value={neededByDate}
                                        placeholder="Pick a day"
                                        format="MM-dd-yyyy"
                                        onChange={date => {
                                            setNeededByDate(date)
                                        }}
                                        disabledDate={time =>
                                            time.getTime() < Date.now() - 8.64e7
                                        }
                                    />
                                </div>

                                <Field
                                    label="How many?"
                                    name="quantity"
                                    component={LabeledInputField}
                                    type="number"
                                    placeholder=""
                                />

                                <ShiptoDropdown
                                    label="Where do you need it?"
                                    name="shipToAccountId"
                                    shiptos={selectOptions}
                                />

                                <div
                                    id="upload"
                                    className="margin-bottom-small"
                                ></div>

                                <Upload
                                    className="upload-demo"
                                    drag
                                    accept="image/*"
                                    capture="camera"
                                    action="/ajax/request/upload"
                                    showFileList={false}
                                    tip={
                                        <div className="el-upload__tip">
                                            jpg/png files with a size less than
                                            8mb
                                        </div>
                                    }
                                    onSuccess={(res, file) =>
                                        handleImageSuccess(res, file)
                                    }
                                    onError={result => handleImageError(result)}
                                    onProgress={() => showLoader()}
                                >
                                    <i className="el-icon-upload"></i>
                                    <div
                                        className="el-upload__text"
                                        id="upload-btn-text"
                                    >
                                        Click to Upload
                                    </div>
                                </Upload>

                                <Spacer size="small" />

                                <div className="row">
                                    <div className="columns small-11 small-centered">
                                        <Button
                                            nativeType="submit"
                                            type="primary"
                                            className="expanded"
                                            disabled={
                                                pristine ||
                                                submitting ||
                                                impersonatedBy === 'salesperson'
                                            }
                                        >
                                            Send
                                        </Button>
                                    </div>
                                </div>
                                <Spacer size="medium" />
                            </form>
                        </>
                    )}
                </Form>
            </MainSectionDefault>
        </AppContainer>
    )
}

PartRequestPage.propTypes = {
    location: PropTypes.shape({
        search: PropTypes.string,
        state: PropTypes.shape({
            source: PropTypes.string,
        }),
    }),
}

export default PartRequestPage
